import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Services = () => {
  const personalServices = [
    'Pranic Healing & Energy Diagnostics',
    'Guided Meditation & Inner Peace',
    'Emotional Wellness & Trauma Release',
    'Stress & Burnout Recovery',
    'Chakra & Aura Cleansing',
  ];

  const workplaceServices = [
    'Corporate Wellness Days & Workshops',
    '6-Month Customized Wellness Program',
    'Stress & Burnout Management',
    'Leadership Wellness & Executive Coaching',
    'Mass Healing & Group Energy Work',
  ];

  return (
    <section id="services" className="scroll-mt-24 bg-lotus-petal/20 px-6 py-20 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex flex-col items-start justify-between gap-6 lg:mb-16 lg:flex-row lg:items-end"
        >
          <div className="max-w-xl">
            <span className="mb-4 block text-xs font-medium uppercase tracking-[0.3em] text-lotus-deep/80">
              Our Service Offerings
            </span>
            <h2 className="font-serif text-3xl leading-tight text-lotus-ink sm:text-4xl lg:text-5xl">
              Healing Pathways
            </h2>
            <p className="mt-4 text-lotus-ink/60">
              From individual energy cleansing to organizational burnout management, explore our dedicated packages on separate pages below.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-lotus-deep" />
            <span className="text-[10px] uppercase tracking-widest text-lotus-ink/60 font-medium">
              Holistic Wellness
            </span>
          </div>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Card 1: Individual Wellness */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.1 }}
            whileHover={{ y: -6 }}
            className="group flex flex-col justify-between border border-lotus-petal bg-white p-8 transition-all duration-300 hover:border-lotus-deep/60 hover:shadow-xl sm:p-12 rounded-3xl"
          >
            <div>
              <span className="mb-6 block font-serif text-xl italic text-lotus-deep/80">
                01 / Individual Wellness
              </span>
              <h3 className="mb-6 font-serif text-2xl text-lotus-ink group-hover:text-lotus-deep transition-colors">
                Personal Restoration Packages
              </h3>
              <ul className="space-y-4 mb-8">
                {personalServices.map((item, idx) => (
                  <motion.li 
                    key={item} 
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + idx * 0.05 }}
                    className="flex items-center gap-3 text-lotus-ink text-sm sm:text-base"
                  >
                    <div className="size-1.5 rounded-full bg-lotus-deep shrink-0 group-hover:scale-125 transition-transform" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="pt-6 border-t border-lotus-petal/60 flex flex-col sm:flex-row gap-3">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <Link
                  to="/individual-wellness"
                  className="flex items-center justify-center gap-2 rounded-full bg-lotus-deep px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white transition-all hover:bg-lotus-deep/90 shadow-sm active:scale-95 w-full"
                >
                  <span>View 3 Individual Packages</span>
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>

              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <a
                  href="https://api.whatsapp.com/send?phone=+919088771406&text=Hi%20Lotus%20Prana%2C%20I%20would%20like%20to%20enquire%20about%20Individual%20Wellness%20Packages."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full border border-lotus-deep/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-lotus-deep hover:bg-lotus-petal/40 transition-colors w-full"
                >
                  <MessageCircle size={15} />
                  <span>WhatsApp Us</span>
                </a>
              </motion.div>
            </div>
          </motion.div>

          {/* Card 2: Corporate Wellness */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, delay: 0.25 }}
            whileHover={{ y: -6 }}
            className="group flex flex-col justify-between border border-lotus-petal bg-white p-8 transition-all duration-300 hover:border-lotus-deep/60 hover:shadow-xl sm:p-12 rounded-3xl"
          >
            <div>
              <span className="mb-6 block font-serif text-xl italic text-lotus-deep/80">
                02 / Corporate & Organizational Wellness
              </span>
              <h3 className="mb-6 font-serif text-2xl text-lotus-ink group-hover:text-lotus-deep transition-colors">
                Workplace Flourishing Programs
              </h3>
              <ul className="space-y-4 mb-8">
                {workplaceServices.map((item, idx) => (
                  <motion.li 
                    key={item} 
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + idx * 0.05 }}
                    className="flex items-center gap-3 text-lotus-ink text-sm sm:text-base"
                  >
                    <div className="size-1.5 rounded-full bg-lotus-deep shrink-0 group-hover:scale-125 transition-transform" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="pt-6 border-t border-lotus-petal/60 flex flex-col sm:flex-row gap-3">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <Link
                  to="/corporate-wellness"
                  className="flex items-center justify-center gap-2 rounded-full bg-lotus-deep px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white transition-all hover:bg-lotus-deep/90 shadow-sm active:scale-95 w-full"
                >
                  <span>Explore Corporate Offerings</span>
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>

              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.95 }} className="w-full sm:w-auto">
                <a
                  href="https://api.whatsapp.com/send?phone=+919088771406&text=Hi%20Lotus%20Prana%2C%20I%20would%20like%20to%20request%20a%20Corporate%20Proposal%20for%20our%20organization."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-full border border-lotus-deep/40 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-lotus-deep hover:bg-lotus-petal/40 transition-colors w-full"
                >
                  <MessageCircle size={15} />
                  <span>WhatsApp Proposal</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;
